import type {
  EvaluationFinality,
  Recommender,
  RoundAggregate,
} from "@engine/round/aggregate";
import { isZeroMVF, type MVF } from "@lib/tranche";

// Display copy for the three finality verdicts, kept here (not in a component
// file) so non-React surfaces — the audit log humanizer, exports — can share
// it.
export const FINALITY_LABELS: Record<
  EvaluationFinality,
  {
    /** Compact chip/badge label. */
    short: string;
    /**
     * The recommender-voiced statement the verdict records — quoted back on
     * the settled row and in the read-back chip's bubble, and read by funders.
     * Keep it a complete sentence: it appears inside quotation marks.
     */
    statement: string;
    /**
     * A one-character mark for the read-back chip, where the label is short
     * and rows are dense. Only the two verdicts that place the proposal on
     * the value scale get one; "no evaluation" is a claim about effort, not
     * value, and giving it a mark from the same vocabulary put all three on a
     * scale it doesn't belong to — a leading "—" read as "negative" when it
     * meant "none".
     */
    mark?: string;
  }
> = {
  "not-evaluated": {
    short: "No evaluation",
    statement: "I haven't done a meaningful evaluation of this proposal",
  },
  "negligible-value": {
    short: "Zero value",
    statement: "I think this proposal is of zero or negligible value",
    mark: "0",
  },
  "negative-value": {
    short: "Negative value",
    statement: "I think this proposal is of negative value",
    mark: "\u2193",
  },
};

/**
 * Whether an MVF expresses real value for the evaluable: it exists, isn't a
 * flat-zero curve, and starts above the recommender's hold line. MVF curves
 * are non-increasing, so "the whole curve is at or below hold" is exactly
 * `mvf[0].value <= holdValue`.
 *
 * Shared by the reducer (which both refuses to record a verdict over a
 * meaningfully-valued curve and clears the verdict when a curve becomes
 * meaningfully valued) and the client availability gate, so the two can't
 * drift.
 */
export function mvfIsMeaningfullyValued(
  mvf: MVF | undefined | null,
  holdValue: number,
): boolean {
  if (!mvf || isZeroMVF(mvf)) {
    return false;
  }

  return (mvf[0]?.value ?? 0) > holdValue;
}

/**
 * Whether the curve stands in the way of closing out — the *product* rule,
 * as opposed to the reducer's historical one above.
 *
 * Any curve that isn't flat zero blocks it. Deliberately stricter than
 * `curveBlocksFinality`: a curve peaking below the hold line earns no money,
 * but it is still a curve the recommender drew, and closing out is for
 * proposals you leave without one. Treating "below hold" as a special
 * half-available case produced a panel whose availability nobody could
 * predict from looking at the chart.
 *
 * Enforced by the client gate ONLY, never by the reducer: it is stricter
 * than the rule the ledger was written under, and a reducer runs over history
 * (see "Every confirmed action replays forever" in src/engine/AGENTS.md).
 * Being stricter than the reducer is the safe direction — it hides a control
 * the engine would have accepted, rather than offering one it will refuse.
 */
export function curveBlocksCloseOut(mvf: MVF | undefined | null): boolean {
  return mvf != null && !isZeroMVF(mvf);
}

/**
 * Whether the recommender may declare a finality verdict on `evaluableId`:
 * only while their curve for it is absent or zero, and their evaluation
 * isn't deferred or recused.
 *
 * This is the product rule, and it is deliberately STRICTER than the gate the
 * reducer enforces. The reducer may only hold rules that were true for every
 * action ever recorded, so it keeps the original at/below-hold rule; this
 * gate and the admission door carry the current one. Keep them in step with
 * each other — they are what a user experiences — and never push either down
 * into the reducer.
 *
 * Retracting a verdict is deliberately NOT gated by any of this: if the hold
 * line later moves, the proposal is withdrawn, or the evaluation becomes
 * deferred/recused, the standing verdict is kept (a verdict is a statement
 * the recommender made, not a derived fact) and the recommender must always
 * be able to clear it.
 */
export function canDeclareFinality(
  round: RoundAggregate,
  recommender: Recommender,
  evaluableId: string,
): boolean {
  const evaluationState = recommender.evaluations[evaluableId]?.state;

  if (evaluationState === "deferred" || evaluationState === "recused") {
    return false;
  }

  return !curveBlocksCloseOut(recommender.mvfProfile[evaluableId]);
}

/**
 * Whether a standing negative-value claim needs its own chip beside the
 * verdict chip, or would just say the same thing twice.
 *
 * The legacy `negative-value` verdict and the newer claim ARE the same
 * statement recorded two ways, so an evaluation carrying both should read as
 * one claim, not two identical badges. Reachable only for an evaluation that
 * already held that verdict before the checkbox replaced the close-out strip
 * — the strip is gone, so no new verdicts can be made — but the read-back
 * surfaces shouldn't show the seam either way.
 *
 * Lives here rather than in each of the three surfaces (proposals list,
 * proposal header, All Notes) so the rule can't drift between them, and so
 * it is one deletion when the legacy verdicts are retired.
 */
export function showsNegativeValueChip(
  finality: EvaluationFinality | null | undefined,
  negativeValue: boolean | undefined,
): boolean {
  return negativeValue === true && finality !== "negative-value";
}
